/* Preorder cart adapter. Storefront activation requires the disabled-by-default
   theme setting and an authenticated, same-origin projection endpoint. */
(function (root) {
  'use strict';
  const contract = root.AlAsilPurchaseOptions;
  if (!contract) return;
  const error = (code, message) => Object.assign(new Error(message), { code });
  function create({ loadOffers, review, loadCart, now = () => new Date().toISOString() }) {
    function assertOffer(offer) {
      const start=Date.parse(offer.dispatchStart),end=Date.parse(offer.dispatchEnd),at=Date.parse(now());
      if(offer.version!==1 || !['preorder','backorder'].includes(offer.kind) || !Number.isSafeInteger(offer.revision) || offer.revision<1 || !Number.isFinite(start) || !Number.isFinite(end) || start>end || !Number.isFinite(at) || (offer.startsAt && !Number.isFinite(Date.parse(offer.startsAt))) || (offer.endsAt && !Number.isFinite(Date.parse(offer.endsAt))))throw error('invalid_projection','Preorder terms could not be verified.');
      if(offer.status!=='live'||offer.purchasable!==true||at>=end||(offer.startsAt&&at<Date.parse(offer.startsAt))||(offer.endsAt&&at>=Date.parse(offer.endsAt)))throw error('offer_unavailable','This preorder is not currently available.');
    }
    async function prepareItems(source, requireReview = false) {
      const items = source.map(item => ({...item, properties: {...item.properties}}));
      const offers = await loadOffers([...new Set(items.map(contract.variantId))]);
      for (const item of items) {
        const reviewRequired = requireReview || item.preorder_requires_review === true || item.preorder_requires_review === 'true';
        delete item.preorder_requires_review;
        const matches = offers.filter(o => o.variantIds.some(v => contract.numeric(v,'ProductVariant') === contract.variantId(item)));
        if (matches.length > 1) throw error('offer_ambiguous','This product has conflicting preorder terms. Please try later.');
        const offer = matches[0];
        if (!offer) {
          if (contract.isPreorder(item)) throw error('offer_unavailable','This preorder is no longer available. Review your cart.');
          continue;
        }
        assertOffer(offer);
        if (!Number.isInteger(Number(item.quantity)) || Number(item.quantity)<1 || Number(item.quantity)>offer.perOrderLimit) throw error('per_order_limit','Check the quantity limit for this preorder.');
        const p=item.properties, prefix=contract.PREFIX;
        if (contract.isPreorder(item)) {
          if(p[prefix+'offer']!==offer.offerId || String(p[prefix+'revision'])!==String(offer.revision) || contract.planId(item)!==contract.numeric(offer.planId,'SellingPlan')) {
            throw error('terms_changed','Preorder terms changed. Remove this line and review the product again.');
          }
          if(reviewRequired && !await review(offer,item))throw error('review_cancelled','Preorder was not added.');
        } else {
          // Never silently replace a subscription/other explicitly chosen plan.
          if(contract.planId(item))throw error('plan_conflict','This purchase option cannot be combined with the preorder offer.');
          if(!await review(offer,item))throw error('review_cancelled','Preorder was not added.');
          // A metafield-driven offer carries no plan; never send an empty selling_plan.
          const plan=contract.numeric(offer.planId,'SellingPlan');
          if(plan)item.selling_plan=plan;
          item.properties={...p,[prefix+'offer']:offer.offerId,[prefix+'revision']:String(offer.revision)};
        }
        contract.assertTerms(item);
      }
      const counts=new Map();
      for(const item of items) if(contract.isPreorder(item)) {
        const id=item.properties[contract.PREFIX+'offer'];counts.set(id,(counts.get(id)||0)+Number(item.quantity));
      }
      if(loadCart && counts.size){
        const cart=await loadCart();if(!Array.isArray(cart?.items))throw error('cart_unverified','The cart could not be verified.');
        for(const existing of cart.items){
          const id=existing.properties?.[contract.PREFIX+'offer'];
          if(counts.has(id))counts.set(id,counts.get(id)+Number(existing.quantity));
        }
      }
      for(const [id,count] of counts){const offer=offers.find(o=>o.offerId===id);if(!Number.isSafeInteger(count)||!Number.isSafeInteger(offer.perOrderLimit)||!Number.isSafeInteger(offer.remaining)||count>offer.perOrderLimit || count>offer.remaining)throw error('capacity_exhausted','This request exceeds the available preorder allocation.');}
      return items;
    }
    async function prepareForm(form) {
      const [item] = await prepareItems([contract.fromFormData(form)], form.get('preorder_requires_review') === 'true');
      const result = new FormData();for(const [key,value] of form.entries())result.append(key,value);
      result.delete('preorder_requires_review');
      if(item.selling_plan)result.set('selling_plan',item.selling_plan);
      for(const [key,value] of Object.entries(item.properties || {}))result.set(`properties[${key}]`,value);
      return result;
    }
    async function validateQuantityEdit(path, body) {
      if(!loadCart)throw error('cart_unverified','The cart must be verified before changing quantities.');
      const cart=await loadCart();
      if(!Array.isArray(cart?.items))throw error('cart_unverified','The cart could not be verified.');
      const items=cart.items.map(item=>({...item,properties:{...item.properties}}));
      function target(key) {
        const exact=items.find(item=>item.key===String(key));if(exact)return exact;
        const matches=items.filter(item=>contract.variantId(item)===String(key));
        if(matches.length!==1)throw error('ambiguous_line','Refresh your cart before changing this line.');
        return matches[0];
      }
      let increase=false;
      function quantity(item,value){
        const n=Number(value);
        if(!item||!Number.isSafeInteger(n)||n<0)throw error('invalid_quantity','Check the requested quantity.');
        increase=increase||n>Number(item.quantity);item.quantity=n;
      }
      if(/\/change(?:\.js)?$/.test(path)) {
        const item=body.id!=null?target(body.id):items[Number(body.line)-1];
        quantity(item,body.quantity === undefined ? item?.quantity : body.quantity);
        if(body.selling_plan!==undefined || body.properties!==undefined){
          if(contract.isPreorder(item))throw error('terms_changed','Change purchase options from the product page.');
          increase=true;
          if(body.properties!==undefined)item.properties=body.properties||{};
          if(body.selling_plan!==undefined)item.selling_plan=body.selling_plan;
        }
      }else if(Array.isArray(body.updates)){
        if(body.updates.length!==items.length)throw error('cart_unverified','Refresh your cart before changing quantities.');
        body.updates.forEach((value,index)=>quantity(items[index],value));
      }else{
        for(const [key,value] of Object.entries(body.updates||{}))quantity(target(key),value);
      }
      // Decreases/removals remain possible when a campaign has expired or paused.
      if(!increase)return;
      const active=items.filter(item=>Number(item.quantity)>0);
      const offers=await loadOffers([...new Set(active.map(contract.variantId))]);
      const counts=new Map();
      for(const item of active){
        const matches=offers.filter(o=>o.variantIds.some(v=>contract.numeric(v,'ProductVariant')===contract.variantId(item)));
        if(matches.length>1)throw error('offer_ambiguous','Conflicting preorder terms.');
        const offer=matches[0];
        if(!offer){if(contract.isPreorder(item))throw error('offer_unavailable','This preorder is no longer available.');continue;}
        if(!contract.isPreorder(item) || item.properties[contract.PREFIX+'offer']!==offer.offerId || String(item.properties[contract.PREFIX+'revision'])!==String(offer.revision) || contract.planId(item)!==contract.numeric(offer.planId,'SellingPlan'))throw error('terms_changed','Review the latest purchase options before increasing quantity.');
        assertOffer(offer);
        counts.set(offer.offerId,(counts.get(offer.offerId)||0)+Number(item.quantity));
      }
      for(const [id,count] of counts){const offer=offers.find(o=>o.offerId===id);if(!Number.isSafeInteger(offer.perOrderLimit)||!Number.isSafeInteger(offer.remaining)||count>offer.perOrderLimit||count>offer.remaining)throw error('capacity_exhausted','This quantity exceeds the available preorder allocation.');}
    }
    async function prepareRequest(url, options) {
      const path=new URL(url,root.location.origin).pathname;
      if(/\/cart\/(?:change|update)(?:\.js)?$/.test(path)) {
        if(options.body instanceof FormData)throw error('cart_unverified','Refresh your cart before changing quantities.');
        const body=JSON.parse(options.body);
        // A pure removal cannot exceed capacity or change purchase terms.
        // Let Shopify resolve the line without a prerequisite cart read.
        const pureRemoval = /\/change(?:\.js)?$/.test(path)
          && body.quantity === 0 && body.properties === undefined && body.selling_plan === undefined
          && body.updates === undefined
          && (typeof body.id === 'string' && body.id.length > 0 || Number.isInteger(body.line) && body.line > 0);
        if(pureRemoval)return options;
        if(body.quantity!==undefined || body.updates!==undefined || body.properties!==undefined || body.selling_plan!==undefined)await validateQuantityEdit(path,body);
        return options;
      }
      if(!/\/cart\/add(?:\.js)?$/.test(path))return options;
      if(options.body instanceof FormData)return {...options,body:await prepareForm(options.body)};
      const body=JSON.parse(options.body);
      const items=await prepareItems(body.items || [body]);
      return {...options,body:JSON.stringify(body.items?{...body,items}:{...body,...items[0]})};
    }
    return {prepareItems,prepareForm,prepareRequest};
  }
  async function review(offer, item) {
    const ar=document.documentElement.lang.startsWith('ar');
    if(typeof HTMLDialogElement==='undefined')return false;
    return new Promise(resolve=>{
      const previous=document.activeElement, dialog=document.createElement('dialog');
      dialog.className='preorder-review';
      dialog.dir=ar?'rtl':'ltr';dialog.setAttribute('aria-label',ar?'مراجعة الطلب المسبق':'Review preorder');
      const title=document.createElement('h2');title.textContent=ar?'مراجعة الطلب المسبق':'Review pre-order';title.id='preorder-review-title';dialog.setAttribute('aria-labelledby',title.id);
      const copy=document.createElement('p');copy.textContent=offer.copy[ar?'ar':'en'];
      const dates=document.createElement('p');const format=value=>new Intl.DateTimeFormat(ar?'ar-AE':'en-AE',{dateStyle:'medium',timeZone:'Asia/Dubai'}).format(new Date(value));
      dates.className='preorder-review__dispatch';
      const dispatchLabel=document.createElement('span'),dispatchDates=document.createElement('strong');
      dispatchLabel.textContent=ar?'الشحن المتوقع':'Expected dispatch';
      dispatchDates.textContent=format(offer.dispatchStart)+' – '+format(offer.dispatchEnd);
      dates.append(dispatchLabel,dispatchDates);
      const money=document.createElement('p');money.textContent=offer.paymentMode==='full'?(ar?'الدفع الكامل الآن.':'Full payment now.'):
        (ar?`الدفع الآن: ${offer.paymentMode==='zero'?0:offer.depositPercent}٪. الرصيد مستحق في `:`Pay ${offer.paymentMode==='zero'?0:offer.depositPercent}% now. Balance due `)+format(offer.balanceDue);
      const amount=document.createElement('dl');amount.className='preorder-review__amounts';
      const total=offer.priceMinor*Number(item.quantity || 1), due=offer.paymentMode==='full'?total:offer.paymentMode==='zero'?0:Math.round(total*offer.depositPercent/100);
      const currency=value=>new Intl.NumberFormat(ar?'ar-AE':'en-AE',{style:'currency',currency:offer.currency}).format(value/100);
      for(const [label,value] of [[ar?'قيمة المنتجات':'Items subtotal',total],[ar?'المستحق الآن':'Due now',due],[ar?'الرصيد المتبقي':'Remaining balance',total-due]]){
        const row=document.createElement('div'),term=document.createElement('dt'),price=document.createElement('dd');
        term.textContent=label;price.textContent=currency(value);row.append(term,price);amount.append(row);
      }
      const policy=document.createElement('p');policy.textContent=ar?'يُشحن طلبك كاملاً معاً. استرداد كامل عند الإلغاء قبل تجهيز الشحنة.':'Your order ships together. Full refund if cancelled before shipping preparation.';
      const cancel=document.createElement('button'),accept=document.createElement('button');cancel.textContent=ar?'رجوع':'Go back';accept.textContent=ar?'الموافقة والإضافة':'Agree and add';
      cancel.type=accept.type='button';cancel.className='preorder-review__back';accept.className='preorder-review__accept';
      copy.className='preorder-review__copy';money.className='preorder-review__payment';policy.className='preorder-review__policy';
      const actions=document.createElement('div');actions.className='preorder-review__actions';actions.append(cancel,accept);
      let settled=false;const finish=value=>{if(settled)return;settled=true;dialog.close();dialog.remove();previous?.focus();resolve(value);};
      cancel.onclick=()=>finish(false);accept.onclick=()=>finish(true);dialog.oncancel=e=>{e.preventDefault();finish(false);};
      dialog.append(title,copy,dates,money,amount,policy,actions);document.body.append(dialog);dialog.showModal();cancel.focus();
    });
  }
  function configureStorefront(endpoint) {
    const url=new URL(endpoint,root.location.origin);
    if(url.origin!==root.location.origin || !/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(?:apps|a|community|tools)\/[a-z0-9-]+\/preorders\/resolve$/i.test(url.pathname) || url.search || url.hash)throw error('invalid_endpoint','Invalid preorder endpoint.');
    root.alasilPreorderCartGuard=create({review,loadCart:async()=>{
      if (typeof root.getCartSnapshot === 'function') {
        const cart = await root.getCartSnapshot();
        if (!Array.isArray(cart?.items)) throw error('cart_unverified','The cart could not be verified.');
        return cart;
      }
      const response=await fetch((root.Shopify?.routes?.root || '/')+'cart.js',{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(10000)});
      if(!response.ok)throw error('cart_unverified','The cart could not be verified.');return response.json();
    },loadOffers:async variantIds=>{
      // Native verification permits a 30-second read; do not cancel a valid
      // response at 10 seconds while the server is still verifying it.
      let response;
      try {
        response=await fetch(url.href,{method:'POST',credentials:'same-origin',redirect:'error',cache:'no-store',signal:AbortSignal.timeout(35000),headers:{'Content-Type':'application/json'},body:JSON.stringify({variantIds})});
      } catch (cause) {
        throw error('offers_unavailable',document.documentElement?.lang==='ar'?'تعذر التحقق من الطلب المسبق. يرجى المحاولة مرة أخرى.':'We could not verify this preorder. Please try again.');
      }
      if(!response.ok)throw error('offers_unavailable','Preorder terms could not be verified. Please try again.');
      const body=await response.json();if(!Array.isArray(body.offers))throw error('invalid_projection','Preorder terms could not be verified.');
      return body.offers;
    }});
    return root.alasilPreorderCartGuard;
  }
  root.AlAsilPreorderCart={create,review,configureStorefront,configureLocal(options){
    if(!['localhost','127.0.0.1','[::1]'].includes(root.location.hostname))throw error('local_only','Preorder integration is local-only.');
    root.alasilPreorderCartGuard=create({review,...options});return root.alasilPreorderCartGuard;
  }};
  const endpoint=document.currentScript?.dataset.preorderEndpoint;
  if(endpoint)configureStorefront(endpoint);
})(window);
